//식단표 이미지 만들기 - 주차 계산·휴무 판정·썸네일 음식 자동 추천 (목업 meal-plan-download.html 규칙 그대로)

export const DAYS = ["월", "화", "수", "목", "금", "토"];
export const WEEK_COLORS: { [week: number]: string } = { 1: "#FDE5DD", 2: "#FFF5D6", 3: "#E5F6DA", 4: "#DCEEFB", 5: "#E2E1FB" };

export type ImageKey = "thumb" | "banner" | "reg" | "sp";
export const ORDER_K: ImageKey[] = ["thumb", "banner", "reg", "sp"];
export const NAMES: { [k: string]: string } = { thumb: "1_썸네일", banner: "2_본문상단", reg: "3_정기식", sp: "4_특식" };
export const LABEL: { [k: string]: string } = { thumb: "썸네일", banner: "본문 상단", reg: "정기식", sp: "특식" };

export interface SpItem { store: string; menu: string; }

//서버 GET /dashboard/mealplanimage/week 응답
export interface RegData { days: { lunch: string[], dinner: string[] }[]; registeredAt: string | null; registeredBy: string | null; isShared: boolean; }
export interface SpData { lunch: SpItem[][]; dinner: SpItem[][]; registeredAt: string | null; registeredBy: string | null; }

//이미지·표에서 쓰는 한 주차 뷰 (목업 D() 와 같은 모양)
export interface WeekView {
    month: number;
    week: number;
    reg: { lunch: string[][], dinner: string[][] } | null;
    sp: { lunch: (SpItem | null)[], dinner: (SpItem | null)[] } | null;
    hol: { reg: boolean[], sp: boolean[] };
    holText: { reg: string[], sp: string[] };
    emoji: string | null;
    scale: number;
}

const pad = (n: number) => (n < 10 ? "0" + n : "" + n);

export const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseKey = (key: string) => {
    const [y, m, d] = key.split("-").map((e) => +e);
    return new Date(y, m - 1, d);
};

export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

//그 주 월요일 (일요일은 지난 월요일 주)
export const mondayOf = (d: Date) => {
    const day = d.getDay();
    return addDays(d, day === 0 ? -6 : 1 - day);
};

//"10월 4주차" — 월요일 기준 월, 월요일 날짜 / 7 올림 (10/26 → 4주차, 11/2 → 1주차, 8/31 → 5주차)
export const weekInfo = (monday: Date) => ({ month: monday.getMonth() + 1, week: Math.ceil(monday.getDate() / 7) });

export const fmt = (d: Date) => `${d.getMonth() + 1}.${pad(d.getDate())}`;

//정기식 휴무 — 중식 첫 칸에 '휴무'/'공휴일' 글자가 있고 그날 나머지 칸이 비어 있으면 휴무 (문구 = 그 글자)
const HOLIDAY_RE = /휴무|공휴일/;
const regHoliday = (day: { lunch: string[], dinner: string[] }) => {
    const first = day.lunch[0] || "";
    if (!HOLIDAY_RE.test(first) || day.lunch.length !== 1) return null;
    if (day.dinner.length > 1 || (day.dinner.length === 1 && day.dinner[0] !== first)) return null;
    return first;
};

//서버 응답 + 특식 칸별 선택(index) → 한 주차 뷰
export const buildWeekView = (monday: Date, reg: RegData | null, sp: SpData | null, spPick: { [slot: string]: number }, emoji: string | null, scale: number): WeekView => {
    const { month, week } = weekInfo(monday);
    const holReg = [false, false, false, false, false, false];
    const holTextReg = ["", "", "", "", "", ""];
    const holSp = [false, false, false, false, false, false];
    const holTextSp = ["", "", "", "", "", ""];

    let regView: WeekView["reg"] = null;
    if (reg) {
        regView = { lunch: [], dinner: [] };
        reg.days.forEach((day, i) => {
            regView!.lunch.push(day.lunch);
            regView!.dinner.push(day.dinner);
            const text = regHoliday(day);
            if (text) { holReg[i] = true; holTextReg[i] = text; }
        });
    }

    let spView: WeekView["sp"] = null;
    if (sp) {
        spView = { lunch: [], dinner: [] };
        for (let i = 0; i < 6; i++) {
            (["lunch", "dinner"] as const).forEach((m) => {
                const list = sp[m][i] || [];
                const pick = spPick[`${i}-${m}`] || 0;
                spView![m].push(list.length ? (list[pick] || list[0]) : null);
            });
            //특식 휴무 — 월~금 중 점심·저녁 모두 등록이 없는 날 (토요일·한 끼만 빈 칸은 '—')
            if (i < 5 && !(sp.lunch[i] || []).length && !(sp.dinner[i] || []).length) {
                holSp[i] = true; holTextSp[i] = "휴무";
            }
        }
    }

    return {
        month, week,
        reg: regView,
        sp: spView,
        hol: { reg: holReg, sp: holSp },
        holText: { reg: holTextReg, sp: holTextSp },
        emoji,
        scale
    };
};

/* 자동 추천 키워드 표 (식단표 만들기 도구와 동일, 순서 = 우선순위) */
const KEYWORDS: [string, string[]][] = [
    ["떡볶이", ["떡볶이", "떡튀순", "떡볶"]], ["김밥", ["김밥"]], ["비빔밥", ["비빔밥", "비빔"]], ["오므라이스", ["오므라이스", "오므"]],
    ["카레라이스", ["카레"]], ["짜장밥", ["짜장", "짜파"]], ["짬뽕", ["짬뽕"]], ["냉면", ["냉면", "밀면", "막국수"]], ["쌀국수", ["쌀국수", "팟타이", "분짜"]],
    ["우동", ["우동", "소바", "칼국수", "국수"]], ["파스타", ["파스타", "리조또", "스파게티", "도리아"]], ["피자", ["피자"]], ["버거", ["버거"]],
    ["샌드위치", ["샌드위치", "토스트", "베이글", "파니니", "바게트"]], ["타코", ["타코", "브리또", "부리또", "케밥", "치미창가"]],
    ["초밥", ["초밥", "스시", "회덮밥", "사케동"]], ["주먹밥", ["주먹밥", "유부초밥", "오니기리"]], ["돈까스", ["돈까스", "돈카츠", "카츠", "까스"]],
    ["함박스테이크", ["함박", "스테이크"]], ["깐풍기", ["깐풍", "탕수", "강정", "가라아게"]], ["치킨", ["치킨", "닭다리"]], ["새우튀김", ["텐동", "튀김", "덴뿌라", "텐뿌라"]],
    ["만두", ["만두", "딤섬"]], ["삼겹살", ["삼겹", "갈비", "보쌈", "족발", "수육", "대패", "불고기"]], ["제육덮밥", ["제육", "덮밥", "불백", "두루치기", "동"]],
    ["볶음밥", ["볶음밥", "철판", "솥밥"]], ["국밥", ["국밥", "해장국", "찌개", "설렁탕", "순두부", "곰탕", "국"]], ["샐러드", ["샐러드", "포케", "보울"]]
];

const guessEmoji = (text: string) => {
    for (const [name, keys] of KEYWORDS) {
        if (keys.some((k) => text.includes(k))) return name;
    }
    return null;
};

//특식 월~토 점심·저녁(각 칸 첫번째 메뉴) → 정기식 중식 첫 메뉴 순으로 처음 걸리는 것, 없으면 볶음밥
export const autoPick = (reg: RegData | null, sp: SpData | null): { name: string | null, from: string | null } => {
    if (!(reg && sp)) return { name: null, from: null };
    const cands: { t: string, where: string }[] = [];
    DAYS.forEach((_, i) => {
        (["lunch", "dinner"] as const).forEach((m) => {
            const c = (sp[m][i] || [])[0];
            if (c) cands.push({ t: c.store + " " + c.menu, where: `${DAYS[i]} 특식 ${m === "lunch" ? "점심" : "저녁"}` });
        });
    });
    DAYS.forEach((_, i) => {
        const t = ((reg.days[i] && reg.days[i].lunch) || [])[0];
        if (t) cands.push({ t, where: `${DAYS[i]} 정기식 중식` });
    });
    for (const c of cands) {
        const n = guessEmoji(c.t);
        if (n) return { name: n, from: `${c.where} '${c.t}'` };
    }
    return { name: "볶음밥", from: "걸리는 메뉴가 없어 기본값" };
};

//파일명 앞부분 — "송파점_10월4주차", 지점이 다르면 "송파점(정기)·분당점(특식)_10월4주차"
export const fileBase = (regName: string, spName: string, month: number, week: number) => {
    const name = regName === spName ? regName : `${regName}(정기)·${spName}(특식)`;
    return `${name.replace(/\([^)]*호\)/, "")}_${month}월${week}주차`;
};
